class CartDrawer extends HTMLElement {
  // ATOMS: Cart drawer invoice fields
  constructor() {
    super();
    this.openedBy = null;
    this.overlay = null;
    this.panel = null;
    this.closeButtons = [];
    this._focusTrap = null;
    this.invoiceToggleLink = null; // ATOMS: Cart drawer invoice fields
    this.invoiceForm = null; // ATOMS: Cart drawer invoice fields
    this.invoiceRequiredCheckbox = null; // ATOMS: Cart drawer invoice fields
    this.invoiceRequiredFields = []; // ATOMS: Cart drawer invoice fields
    this.checkoutForm = null; // ATOMS: Cart drawer invoice fields
    this.invoiceSavingIndicator = null; // ATOMS: Cart drawer invoice fields
    this.invoiceSaveTimeout = null; // ATOMS: Cart drawer invoice fields
    this.invoiceAttributesAbort = null; // ATOMS: Cart drawer invoice fields
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onClick = this.onClick.bind(this); // ATOMS: Cart drawer invoice fields
    this.onChange = this.onChange.bind(this); // ATOMS: Cart drawer invoice fields
    this.onInput = this.onInput.bind(this); // ATOMS: Cart drawer invoice fields
    this.onSubmit = this.onSubmit.bind(this); // ATOMS: Cart drawer invoice fields
  }

  connectedCallback() {
    this.overlay = this.querySelector("[data-cart-drawer-overlay]");
    this.panel = this.querySelector("[data-cart-drawer-panel]");
    this.closeButtons = Array.from(
      this.querySelectorAll("[data-cart-drawer-close]"),
    );
    this.refreshInvoiceElements(); // ATOMS: Cart drawer invoice fields

    if (this.overlay) {
      this.overlay.addEventListener("click", this.onCloseClick);
    }
    this.closeButtons.forEach((button) =>
      button.addEventListener("click", this.onCloseClick),
    );
    this.addEventListener("click", this.onClick); // ATOMS: Cart drawer invoice fields
    this.addEventListener("change", this.onChange); // ATOMS: Cart drawer invoice fields
    this.addEventListener("input", this.onInput); // ATOMS: Cart drawer invoice fields
    this.addEventListener("submit", this.onSubmit); // ATOMS: Cart drawer invoice fields
    this.syncInvoiceState(); // ATOMS: Cart drawer invoice fields
  }

  disconnectedCallback() {
    if (this.overlay) {
      this.overlay.removeEventListener("click", this.onCloseClick);
    }
    this.closeButtons.forEach((button) =>
      button.removeEventListener("click", this.onCloseClick),
    );
    this.removeEventListener("click", this.onClick); // ATOMS: Cart drawer invoice fields
    this.removeEventListener("change", this.onChange); // ATOMS: Cart drawer invoice fields
    this.removeEventListener("input", this.onInput); // ATOMS: Cart drawer invoice fields
    this.removeEventListener("submit", this.onSubmit); // ATOMS: Cart drawer invoice fields
    if (this.invoiceSaveTimeout) clearTimeout(this.invoiceSaveTimeout);
    if (this.invoiceAttributesAbort) this.invoiceAttributesAbort.abort();
  }

  // ATOMS: Refresh invoice references after cart drawer partial renders.
  refreshInvoiceElements() {
    this.invoiceToggleLink = this.querySelector("[data-invoice-toggle-link]");
    this.invoiceForm = this.querySelector("[data-invoice-form]");
    this.invoiceRequiredCheckbox = this.querySelector(
      "[data-invoice-required-checkbox]",
    );
    this.invoiceRequiredFields = Array.from(
      this.querySelectorAll("[data-invoice-required-field]"),
    );
    this.checkoutForm = this.querySelector("#CartDrawer-CheckoutForm");
    this.invoiceSavingIndicator = this.querySelector("[data-invoice-saving]");
  }

  // ATOMS: Reflect invoice attribute requests in the drawer interface.
  setInvoiceSaving(isSaving) {
    if (!this.invoiceSavingIndicator) return;
    this.invoiceSavingIndicator.classList.toggle("hidden", !isSaving);
    this.invoiceSavingIndicator.setAttribute("aria-hidden", String(!isSaving));
  }

  // ATOMS: Cart drawer invoice fields
  async onClick(event) {
    this.refreshInvoiceElements(); // ATOMS: Cart drawer invoice fields
    const closeButton = event.target.closest("[data-invoice-close]");
    if (closeButton) {
      event.preventDefault();
      this.syncInvoiceState();
      const mustValidateBeforeClose = Boolean(
        this.invoiceRequiredCheckbox && this.invoiceRequiredCheckbox.checked,
      );
      const firstInvalidField = this.invoiceRequiredFields.find(
        (field) => field.required && field.checkValidity && !field.checkValidity(),
      );

      if (mustValidateBeforeClose && firstInvalidField) {
        if (typeof firstInvalidField.reportValidity === "function") {
          firstInvalidField.reportValidity();
        }
        if (typeof firstInvalidField.focus === "function") {
          firstInvalidField.focus();
        }
        return;
      }

      if (this.invoiceForm) {
        // ATOMS: Save completed invoice data before closing.
        await this.saveInvoiceAttributes().catch(() => {});
        this.invoiceForm.classList.add("hidden");
      }
      return;
    }

    const toggleLink = event.target.closest("[data-invoice-toggle-link]");
    if (!toggleLink) return;
    event.preventDefault();
    this.refreshInvoiceElements(); // ATOMS: Cart drawer invoice fields
    if (this.invoiceForm) {
      this.invoiceForm.classList.remove("hidden");
    }
  }

  // ATOMS: Cart drawer invoice fields
  onChange(event) {
    const checkbox = event.target.closest("[data-invoice-required-checkbox]");
    if (!checkbox) {
      if (event.target.closest("[data-invoice-required-field]")) {
        this.saveInvoiceAttributes().catch(() => {});
      }
      return;
    }
    this.refreshInvoiceElements(); // ATOMS: Cart drawer invoice fields
    this.syncInvoiceState();

    // ATOMS: Clear saved invoice attributes when the request is removed.
    if (!checkbox.checked) {
      this.clearInvoiceAttributes().catch(() => {});
      return;
    }

    this.saveInvoiceAttributes().catch(() => {});
  }

  // ATOMS: Save text and select fields without a request per keystroke.
  onInput(event) {
    if (!event.target.closest("[data-invoice-required-field]")) return;
    if (this.invoiceSaveTimeout) clearTimeout(this.invoiceSaveTimeout);
    this.invoiceSaveTimeout = setTimeout(() => {
      this.saveInvoiceAttributes().catch(() => {});
    }, 400);
  }

  // ATOMS: Persist the visible invoice form values in Shopify cart attributes.
  async saveInvoiceAttributes() {
    this.refreshInvoiceElements();
    if (!this.invoiceRequiredCheckbox?.checked) return;
    if (this.invoiceAttributesAbort) this.invoiceAttributesAbort.abort();

    const attributes = { "Invoice Required": "true" };
    this.invoiceRequiredFields.forEach((field) => {
      const attributeName = field.name.match(/^attributes\[(.+)\]$/)?.[1];
      if (attributeName) attributes[attributeName] = field.value;
    });

    const controller = new AbortController();
    this.invoiceAttributesAbort = controller;
    this.setInvoiceSaving(true);
    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ attributes }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error("Cart invoice attributes could not be saved");
      }
    } finally {
      if (this.invoiceAttributesAbort === controller) {
        this.invoiceAttributesAbort = null;
        this.setInvoiceSaving(false);
      }
    }
  }

  // ATOMS: Clear invoice attributes from the Shopify cart.
  async clearInvoiceAttributes() {
    if (this.invoiceSaveTimeout) clearTimeout(this.invoiceSaveTimeout);
    if (this.invoiceAttributesAbort) this.invoiceAttributesAbort.abort();
    const controller = new AbortController();
    this.invoiceAttributesAbort = controller;
    this.setInvoiceSaving(true);
    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          attributes: {
            "Invoice Required": "",
            "Invoice RFC": "",
            "Invoice Business Name": "",
            "Invoice Tax Regime": "",
            "Invoice CFDI Use": "",
            "Invoice Tax ZIP": "",
            "Invoice Email": "",
          },
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error("Cart invoice attributes could not be cleared");
      }
    } finally {
      if (this.invoiceAttributesAbort === controller) {
        this.invoiceAttributesAbort = null;
        this.setInvoiceSaving(false);
      }
    }
  }

  // ATOMS: Cart drawer invoice fields
  onSubmit(event) {
    if (!event.target.matches("#CartDrawer-CheckoutForm")) return;
    this.refreshInvoiceElements(); // ATOMS: Cart drawer invoice fields
    if (!this.invoiceRequiredCheckbox || !this.invoiceRequiredCheckbox.checked) {
      return;
    }

    this.syncInvoiceState();
    const firstInvalidField = this.invoiceRequiredFields.find((field) =>
      field.checkValidity && !field.checkValidity(),
    );

    if (!firstInvalidField) return;

    event.preventDefault();
    if (this.invoiceForm) {
      this.invoiceForm.classList.remove("hidden");
    }
    if (typeof firstInvalidField.reportValidity === "function") {
      firstInvalidField.reportValidity();
    }
    if (typeof firstInvalidField.focus === "function") {
      firstInvalidField.focus();
    }
  }

  // ATOMS: Cart drawer invoice fields
  syncInvoiceState() {
    const invoiceRequested = Boolean(
      this.invoiceRequiredCheckbox && this.invoiceRequiredCheckbox.checked,
    );

    this.invoiceRequiredFields.forEach((field) => {
      field.required = invoiceRequested;
    });

    if (this.invoiceToggleLink) {
      const defaultLabel = this.invoiceToggleLink.dataset.defaultLabel || "";
      const requestedLabel = this.invoiceToggleLink.dataset.requestedLabel || "";
      this.invoiceToggleLink.textContent = invoiceRequested
        ? requestedLabel
        : defaultLabel;
      this.invoiceToggleLink.classList.toggle("text-green-600", invoiceRequested);
      this.invoiceToggleLink.classList.toggle("hover:!text-green-700", invoiceRequested);
      this.invoiceToggleLink.classList.toggle("no-underline", invoiceRequested);
    }
  }

  onCloseClick() {
    this.hide();
  }

  show(opener) {
    this.openedBy = opener || null;
    this.setAttribute("open", "");
    document.documentElement.classList.add("overflow-hidden");
    if (this.overlay) {
      this.overlay.classList.add("opacity-100");
      this.overlay.classList.remove("opacity-0");
    }
    if (this.panel) {
      this.panel.classList.add("!translate-x-0");
    }
    this.classList.remove("pointer-events-none");
    if (!this._focusTrap) {
      this._focusTrap = webvista.createFocusTrap(this, {
        onEscape: this.onCloseClick,
      });
    }
    this.addEventListener("keydown", this._focusTrap);
    webvista.focusFirstElement(this);
  }

  hide() {
    this.removeAttribute("open");
    document.documentElement.classList.remove("overflow-hidden");
    if (this.overlay) {
      this.overlay.classList.remove("opacity-100");
      this.overlay.classList.add("opacity-0");
    }
    if (this.panel) {
      this.panel.classList.remove("!translate-x-0");
    }
    this.classList.add("pointer-events-none");
    if (this._focusTrap) {
      this.removeEventListener("keydown", this._focusTrap);
    }
    if (
      this.openedBy &&
      typeof this.openedBy.focus === "function" &&
      document.body.contains(this.openedBy)
    ) {
      this.openedBy.focus();
    }
  }
}

customElements.define("cart-drawer", CartDrawer);
